/**
 * Qual língua o app está falando agora, e como se troca de língua.
 *
 * O idioma mora no aparelho, e não no servidor, por um motivo só: a porta de
 * senha é a primeira tela, e ela desenha antes de qualquer resposta do
 * `/api/settings`. Um idioma guardado lá fora faria toda abertura começar em
 * português e virar inglês um segundo depois. O `Guardado` de `ligacao.ts` é
 * síncrono exatamente para casos assim -- no celular, quem o enche precisa
 * incluir a chave daqui na lista que é lida antes do primeiro render.
 *
 * Quem não escolheu nada segue o aparelho. É o certo para um app que nunca
 * perguntou nada: um telefone em inglês abre em inglês, e quem não gostar troca
 * nos ajustes e a escolha fica.
 *
 * O `traduzir()` é uma função de módulo, e não um hook, de propósito. São milhares
 * de frases em 244 telas: um `const traduzir = usarIdioma()` em cada componente
 * seria uma linha a mais em cada arquivo e um esquecimento calado em cada
 * arquivo novo. Em vez disso quem assina a mudança é um só -- o `<Idiomas>` na
 * raiz de cada app -- e ele remonta a árvore inteira quando a língua muda.
 * Trocar de idioma é um ato raro e deliberado; perder o estado de uma caixa
 * aberta nesse instante é barato perto de uma tela que fica metade traduzida
 * porque algum `useMemo` no meio do caminho não soube que precisava refazer.
 */

import { Fragment, useSyncExternalStore, type ReactNode } from 'react';

import { ligacao } from '../nucleo/ligacao';
import { LOCALES, traduzirPara, type Idioma, type Valores } from './palavras';

export { NOMES_DE_IDIOMA, LOCALES } from './palavras';
export type { Idioma, Valores } from './palavras';

/**
 * A chave no `Guardado`. Exportada porque o celular precisa nomeá-la na lista
 * do `AsyncStorage.multiGet` que corre antes do primeiro render -- e duas
 * cópias do mesmo texto seriam dois lugares para errar.
 */
export const CHAVE_IDIOMA = 'ns_idioma';

/** `undefined` = ainda não olhei o disco. `null` = não há escolha, siga o aparelho. */
let escolhido: Idioma | null | undefined;
let doAparelho: Idioma | undefined;

const ouvintes = new Set<() => void>();

function ehIdioma(v: unknown): v is Idioma {
  return v === 'pt' || v === 'en';
}

/**
 * A língua do aparelho, olhada uma vez só.
 *
 * `navigator.languages` é a lista do navegador, em ordem de preferência. No
 * celular não existe `navigator`, e a resposta vem do próprio `Intl`, que o
 * Hermes traz com ICU completo -- é a mesma pergunta, feita à única peça que os
 * dois têm. Nada disso é obrigatório a existir, e por isso tudo está dentro de
 * um `try`: um ambiente sem `Intl` abre em português, que é o original.
 */
export function idiomaDoAparelho(): Idioma {
  if (doAparelho) return doAparelho;
  doAparelho = 'pt';
  try {
    const nav = (globalThis as { navigator?: { languages?: readonly string[]; language?: string } })
      .navigator;
    const lista = nav?.languages?.length
      ? nav.languages
      : [nav?.language ?? Intl.DateTimeFormat().resolvedOptions().locale];
    for (const etiqueta of lista) {
      // `\b` casa no hífen, então `pt-BR` e `pt` entram os dois, e `pterodactyl`
      // não entra nenhum.
      if (/^pt\b/i.test(etiqueta)) return (doAparelho = 'pt');
      if (/^en\b/i.test(etiqueta)) return (doAparelho = 'en');
    }
  } catch {
    /* Sem `Intl` e sem navegador: fica o português. */
  }
  return doAparelho;
}

/**
 * O que foi escolhido à mão, ou `null` para "segue o aparelho".
 *
 * A leitura do disco só acontece uma vez, e só depois de `ligar()`. Antes disso
 * -- e só antes -- a resposta é `null` sem ficar guardada: um módulo que
 * perguntasse cedo demais deixaria a escolha da pessoa apagada para o resto da
 * sessão.
 */
export function escolhaDeIdioma(): Idioma | null {
  if (escolhido !== undefined) return escolhido;
  let guardado;
  try {
    guardado = ligacao().guardado;
  } catch {
    return null;
  }
  const v = guardado.ler(CHAVE_IDIOMA);
  escolhido = ehIdioma(v) ? v : null;
  return escolhido;
}

/** A língua que o app está falando agora. */
export function idioma(): Idioma {
  return escolhaDeIdioma() ?? idiomaDoAparelho();
}

/** A etiqueta que o `Intl` entende -- `pt-BR` ou `en-US`. */
export function locale(): string {
  return LOCALES[idioma()];
}

/** `null` volta a seguir o aparelho. */
export function trocarIdioma(novo: Idioma | null): void {
  if (novo === escolhaDeIdioma()) return;
  escolhido = novo;
  try {
    const { guardado } = ligacao();
    if (novo) guardado.gravar(CHAVE_IDIOMA, novo);
    else guardado.apagar(CHAVE_IDIOMA);
  } catch {
    /* Sem ligação a escolha vale só para esta sessão, e é melhor do que nada. */
  }
  for (const f of ouvintes) f();
}

function assinar(f: () => void): () => void {
  ouvintes.add(f);
  return () => {
    ouvintes.delete(f);
  };
}

/**
 * A frase na língua de agora.
 *
 * A chave é a frase em português -- ver `palavras.ts`. Chamar isto com uma
 * frase que ninguém traduziu devolve a própria frase, então envolver uma tela
 * nunca a quebra: no pior caso ela continua em português.
 */
export function traduzir(fonte: string, valores?: Valores): string {
  return traduzirPara(idioma(), fonte, valores);
}

/** Para quem precisa desenhar diferente conforme a língua, e não só traduzir. */
export function usarIdioma(): Idioma {
  return useSyncExternalStore(assinar, idioma, idioma);
}

/** O botão dos ajustes: mostra "Automático" quando não houve escolha. */
export function usarEscolhaDeIdioma(): Idioma | null {
  return useSyncExternalStore(assinar, escolhaDeIdioma, escolhaDeIdioma);
}

/**
 * O único assinante. Na raiz de cada app, por fora de tudo que tem texto.
 *
 * A `key` é o que faz a troca valer: o React joga fora a árvore antiga e monta
 * outra, então toda chamada a `traduzir()` acontece de novo, inclusive as que
 * estavam presas dentro de um `useMemo` ou de um `useState` inicial.
 */
export function Idiomas({ children }: { children: ReactNode }) {
  const atual = usarIdioma();
  return <Fragment key={atual}>{children}</Fragment>;
}
